/**
 * PRD 生成器
 * 使用模板生成结构化的 PRD 文档
 */

import fs from 'fs-extra';
import path from 'path';
import { logger } from '../utils/logger';
import Handlebars from 'handlebars';
import { TaskStore } from './task-store';
import { ExtendedTask } from '../types/extended-task';
import { TaskPriority } from '../types';
// TODO: 恢复使用 @gongfu/memory 包
import { MemoryClient, TaskMemoryAdapter } from '../utils/memory-stub';
import { SmartAgentAllocator } from './smart-agent-allocator';
import { PRDTaskAssociationManager } from './prd-task-association';

export interface PRDGeneratorOptions {
  template?: 'full' | 'simple' | string; // 模板选择
  title: string;
  description: string;
  author?: string;
  version?: string;
  features?: string[];
  constraints?: string[];
  targetUsers?: string[];
  createTasks?: boolean; // 是否自动创建任务
  projectId?: string; // 项目 ID
  aiMode?: 'auto' | 'manual' | 'hybrid' | 'memory-enhanced'; // AI 模式
  userId?: string; // 用户 ID，用于 Memory 系统
}

export interface PRDGenerationResult {
  content: string; // PRD 内容
  id: string; // PRD ID
  path?: string; // 保存路径
  tasks?: ExtendedTask[]; // 创建的任务
}

export class PRDGenerator {
  private templateDir: string;
  private workflowDir: string;
  private taskStore: TaskStore;
  private memoryClient?: MemoryClient;
  private taskMemory?: TaskMemoryAdapter;
  private agentAllocator?: SmartAgentAllocator;
  private prdTaskAssociation: PRDTaskAssociationManager;
  
  constructor(workflowDir?: string, options?: { enableMemory?: boolean; userId?: string; projectId?: string }) {
    const projectRoot = workflowDir || process.cwd();
    this.workflowDir = projectRoot;
    this.templateDir = path.join(projectRoot, '.workflow', 'templates', 'prd');
    this.taskStore = new TaskStore(projectRoot);
    this.prdTaskAssociation = new PRDTaskAssociationManager(projectRoot);
    
    // 初始化 Memory 系统（可选）
    if (options?.enableMemory && options.userId && options.projectId) {
      try {
        this.memoryClient = new MemoryClient({
          userId: options.userId,
          projectId: options.projectId,
        });
        this.taskMemory = new TaskMemoryAdapter({
          userId: options.userId,
          projectId: options.projectId,
        });
        this.agentAllocator = new SmartAgentAllocator({
          enableMemory: true,
          userId: options.userId,
          projectId: options.projectId,
        });
        logger.info('Memory system and agent allocator initialized for PRD generation');
      } catch (error) {
        logger.warn('Failed to initialize memory system:', error);
      }
    } else {
      // 没有 Memory 系统时，仍然创建 Agent 分配器（使用规则模式）
      this.agentAllocator = new SmartAgentAllocator();
    }
  }
  
  /**
   * 从用户输入生成 PRD
   */
  async generateFromIdea(idea: string, options: Partial<PRDGeneratorOptions> = {}): Promise<PRDGenerationResult> {
    logger.info('Generating PRD from idea:', idea);
    
    // 解析用户输入，提取关键信息
    const parsed = this.parseIdea(idea);
    
    // 合并选项
    const finalOptions: PRDGeneratorOptions = {
      template: options.template || 'simple',
      title: options.title || parsed.title,
      description: options.description || parsed.description,
      author: options.author || '@user',
      version: options.version || 'v0.1.0',
      features: options.features || parsed.features,
      constraints: options.constraints || [],
      targetUsers: options.targetUsers || parsed.targetUsers,
      createTasks: options.createTasks !== false, // 默认创建任务
      projectId: options.projectId,
    };
    
    // 生成 PRD
    return this.generate(finalOptions);
  }
  
  /**
   * 使用模板生成 PRD
   */
  async generate(options: PRDGeneratorOptions): Promise<PRDGenerationResult> {
    // 生成 PRD ID
    const prdId = `prd-${Date.now()}`;
    
    // 选择模板
    const templatePath = await this.getTemplatePath(options.template || 'simple');
    
    // 读取模板
    const templateContent = await fs.readFile(templatePath, 'utf-8');
    
    // 准备数据
    const data = {
      ...options,
      id: prdId,
      date: new Date().toISOString().split('T')[0],
      产品名称: options.title,
      背景描述: options.description,
      version: options.version,
      author: options.author,
      
      // 功能列表
      功能1: options.features?.[0] || '核心功能实现',
      功能2: options.features?.[1] || '用户界面设计',
      功能3: options.features?.[2] || '数据存储和管理',
      功能4: options.features?.[3] || '性能优化',
      功能5: options.features?.[4] || '测试和部署',
      
      价值1: '提升用户体验',
      价值2: '提高开发效率',
      价值3: '确保数据安全',
      价值4: '提升系统性能',
      价值5: '保证质量可靠',
      
      // 用户画像
      用户类型1: options.targetUsers?.[0] || '终端用户',
      用户描述1: '日常使用产品的主要用户群体',
      用户类型2: options.targetUsers?.[1] || '管理员',
      用户描述2: '负责系统配置和管理的用户',
      用户类型3: options.targetUsers?.[2] || '开发者',
      用户描述3: '使用 API 或扩展功能的技术用户',
      
      // 其他默认值
      起点: '用户需求',
      步骤1: '需求分析',
      判断: '方案评审',
      条件1: '通过',
      条件2: '需要修改',
      步骤2: '开发实现',
      步骤3: '重新设计',
      终点: '产品发布',
      
      // 非功能需求
      性能指标1: '响应时间 < 200ms',
      性能指标2: '支持 1000+ 并发用户',
      安全要求1: '数据加密传输',
      安全要求2: '用户权限控制',
      兼容性要求1: '支持主流浏览器',
      兼容性要求2: '移动端适配',
      
      // 里程碑
      时间1: '2周',
      交付物1: 'MVP 版本',
      备注1: '核心功能可用',
      时间2: '1个月',
      交付物2: '正式版本',
      备注2: '功能完整',
      时间3: '2个月',
      交付物3: '优化版本',
      备注3: '性能和体验优化',
    };
    
    // 如果有约束条件
    if (options.constraints && options.constraints.length > 0) {
      data['痛点1'] = options.constraints[0];
      data['痛点2'] = options.constraints[1] || '开发周期紧张';
      data['痛点3'] = options.constraints[2] || '资源有限';
    } else {
      data['痛点1'] = '现有解决方案不够完善';
      data['痛点2'] = '用户体验有待提升';
      data['痛点3'] = '缺乏统一的管理工具';
    }
    
    // 编译模板
    const template = Handlebars.compile(templateContent);
    let prdContent = template(data);
    
    // 清理多余的占位符
    prdContent = prdContent.replace(/\{\{[^}]+\}\}/g, (match) => {
      // 为未填充的占位符提供默认值
      const key = match.replace(/[{}]/g, '').trim();
      return `[待补充：${key}]`;
    });
    
    // 在 PRD 内容开头添加元数据
    const metadata = `---
id: ${prdId}
title: ${options.title}
version: ${options.version}
author: ${options.author}
date: ${data.date}
status: draft
---

`;
    prdContent = metadata + prdContent;
    
    // 准备结果
    const result: PRDGenerationResult = {
      content: prdContent,
      id: prdId,
    };
    
    // 如果需要创建任务
    if (options.createTasks) {
      const tasks = await this.createTasksFromPRD(prdId, options);
      result.tasks = tasks;
    }
    
    return result;
  }
  
  /**
   * 解析用户输入的想法
   */
  private parseIdea(idea: string): {
    title: string;
    description: string;
    features: string[];
    targetUsers: string[];
  } {
    // 简单的解析逻辑，未来可以使用 AI 增强
    const sentences = idea.split(/[。.!?]/);
    const firstSentence = sentences[0] || idea;
    
    // 提取标题（通常是第一个动词短语）
    let title = firstSentence;
    if (title.includes('开发')) {
      title = title.split('开发')[1] || title;
    } else if (title.includes('实现')) {
      title = title.split('实现')[1] || title;
    } else if (title.includes('创建')) {
      title = title.split('创建')[1] || title;
    } else if (title.includes('做')) {
      title = title.split('做')[1] || title;
    }
    title = title.trim();
    
    // 识别功能关键词
    const features: string[] = [];
    const featureKeywords = ['支持', '包含', '具有', '能够', '可以', '实现'];
    for (const sentence of sentences) {
      for (const keyword of featureKeywords) {
        if (sentence.includes(keyword)) {
          const feature = sentence.split(keyword)[1];
          if (feature) {
            features.push(feature.trim());
          }
        }
      }
    }
    
    // 识别用户类型
    const targetUsers: string[] = [];
    const userKeywords = ['用户', '开发者', '管理员', '运维', '客户'];
    for (const keyword of userKeywords) {
      if (idea.includes(keyword)) {
        targetUsers.push(keyword);
      }
    }
    
    return {
      title: title || '新产品',
      description: idea,
      features: features.length > 0 ? features : ['核心功能', '用户界面', '数据管理'],
      targetUsers: targetUsers.length > 0 ? targetUsers : ['用户', '管理员'],
    };
  }
  
  /**
   * 获取模板路径
   */
  private async getTemplatePath(template: string): Promise<string> {
    // 如果是预定义模板
    if (template === 'full') {
      return path.join(this.templateDir, 'template.md');
    } else if (template === 'simple') {
      return path.join(this.templateDir, 'simple-template.md');
    }
    
    // 自定义模板路径
    const customPath = path.resolve(template);
    if (await fs.pathExists(customPath)) {
      return customPath;
    }
    
    // 在模板目录中查找
    const templatePath = path.join(this.templateDir, template);
    if (await fs.pathExists(templatePath)) {
      return templatePath;
    }
    
    // 默认使用简单模板
    logger.warn(`Template not found: ${template}, using simple template`);
    return path.join(this.templateDir, 'simple-template.md');
  }
  
  /**
   * 列出可用的模板
   */
  async listTemplates(): Promise<string[]> {
    if (!await fs.pathExists(this.templateDir)) {
      return [];
    }
    
    const files = await fs.readdir(this.templateDir);
    return files.filter(f => f.endsWith('.md'));
  }
  
  /**
   * 智能分配 Agent 到任务
   */
  private async assignAgentToTask(task: ExtendedTask, options: PRDGeneratorOptions): Promise<ExtendedTask> {
    if (this.agentAllocator && options.aiMode !== 'manual') {
      const allocation = await this.agentAllocator.allocateAgents(task);
      task.agentAssignments = {
        primary: allocation.primary,
        reviewers: allocation.reviewers,
        consultants: allocation.consultants,
      };
      
      // 记录分配元数据
      if (allocation.reason || allocation.confidence) {
        task.metadata = {
          ...task.metadata,
          agentAllocationReason: allocation.reason,
          agentAllocationConfidence: allocation.confidence,
          agentFromMemory: allocation.memoryBased || false,
        };
      }
    }
    return task;
  }

  /**
   * 从 PRD 创建关联任务
   */
  private async createTasksFromPRD(prdId: string, options: PRDGeneratorOptions): Promise<ExtendedTask[]> {
    const tasks: ExtendedTask[] = [];
    const baseTask = {
      prdId,
      source: 'prd-generator' as const,
      projectId: options.projectId,
    };
    
    // 获取智能建议（如果启用了 Memory）
    let suggestions: any = null;
    if (this.taskMemory) {
      try {
        const taskDescription = `${options.title} - ${options.description}`;
        suggestions = await this.taskMemory.suggestTask(taskDescription);
        logger.info('Got task suggestions from memory:', suggestions);
      } catch (error) {
        logger.warn('Failed to get task suggestions:', error);
      }
    }
    
    // 1. 需求分析任务
    let analysisTask: ExtendedTask = {
      ...baseTask,
      id: `task-${Date.now()}-analysis`,
      content: `需求分析：${options.title}`,
      status: 'todo',
      priority: 'high' as TaskPriority,
      type: 'analysis',
      tags: ['需求', '分析', 'prd'],
      estimate: suggestions?.estimatedTime?.analysis || '1d',
      acceptanceCriteria: [
        'WHEN 需求分析完成 THEN 产生详细的需求文档',
        'WHEN 评审通过 THEN 所有关键干系人确认需求',
      ],
      // AI 建议的额外信息
      aiMode: options.aiMode || (suggestions ? 'memory-enhanced' : 'auto'),
      metadata: suggestions ? { 
        memorySuggestions: suggestions.similarTasks?.slice(0, 3),
        suggestedApproach: suggestions.approach,
      } : undefined,
    };
    
    // 智能分配 Agent
    analysisTask = await this.assignAgentToTask(analysisTask, options);
    
    analysisTask = await this.taskStore.addTask(analysisTask) as ExtendedTask;
    tasks.push(analysisTask);
    
    // 2. 设计任务
    let designTask: ExtendedTask = {
      ...baseTask,
      id: `task-${Date.now()}-design`,
      content: `设计方案：${options.title}`,
      status: 'todo',
      priority: 'high' as TaskPriority,
      type: 'design',
      tags: ['设计', '架构', 'prd'],
      estimate: '2d',
      dependsOn: [tasks[0].id],
      acceptanceCriteria: [
        'WHEN 设计完成 THEN 产生系统架构图和数据模型',
        'WHEN 技术选型完成 THEN 确定使用的技术栈',
      ],
      aiMode: options.aiMode || 'auto',
    };
    
    // 智能分配 Agent
    designTask = await this.assignAgentToTask(designTask, options);
    
    designTask = await this.taskStore.addTask(designTask) as ExtendedTask;
    tasks.push(designTask);
    
    // 3. 为每个功能创建开发任务
    const devTasks: ExtendedTask[] = [];
    for (let i = 0; i < options.features!.length && i < 5; i++) {
      const feature = options.features![i];
      let devTask: ExtendedTask = {
        ...baseTask,
        id: `task-${Date.now()}-dev-${i}`,
        content: `开发：${feature}`,
        status: 'todo',
        priority: i === 0 ? 'high' as TaskPriority : 'medium' as TaskPriority,
        type: 'development',
        tags: ['开发', 'feature', 'prd'],
        estimate: '3d',
        dependsOn: [tasks[1].id], // 依赖设计任务
        acceptanceCriteria: [
          `WHEN ${feature}开发完成 THEN 功能可正常使用`,
          'WHEN 单元测试通过 THEN 测试覆盖率 > 80%',
        ],
        aiMode: options.aiMode || 'auto',
      };
      
      // 智能分配 Agent
      devTask = await this.assignAgentToTask(devTask, options);
      
      devTask = await this.taskStore.addTask(devTask) as ExtendedTask;
      devTasks.push(devTask);
      tasks.push(devTask);
    }
    
    // 4. 测试任务
    let testTask: ExtendedTask = {
      ...baseTask,
      id: `task-${Date.now()}-test`,
      content: `测试验证：${options.title}`,
      status: 'todo',
      priority: 'high' as TaskPriority,
      type: 'testing',
      tags: ['测试', 'QA', 'prd'],
      estimate: '2d',
      dependsOn: devTasks.map(t => t.id),
      acceptanceCriteria: [
        'WHEN 功能测试完成 THEN 所有功能正常工作',
        'WHEN 性能测试完成 THEN 满足性能指标要求',
        'WHEN 兼容性测试完成 THEN 支持所有目标平台',
      ],
      aiMode: options.aiMode || 'auto',
    };
    
    testTask = await this.assignAgentToTask(testTask, options);
    testTask = await this.taskStore.addTask(testTask) as ExtendedTask;
    tasks.push(testTask);
    
    // 5. 文档任务
    let docTask: ExtendedTask = {
      ...baseTask,
      id: `task-${Date.now()}-doc`,
      content: `编写文档：${options.title}`,
      status: 'todo',
      priority: 'medium' as TaskPriority,
      type: 'documentation',
      tags: ['文档', 'docs', 'prd'],
      estimate: '1d',
      dependsOn: devTasks.map(t => t.id),
      acceptanceCriteria: [
        'WHEN 用户文档完成 THEN 用户可以独立使用产品',
        'WHEN API 文档完成 THEN 开发者可以集成接口',
      ],
      aiMode: options.aiMode || 'auto',
    };
    
    docTask = await this.assignAgentToTask(docTask, options);
    docTask = await this.taskStore.addTask(docTask) as ExtendedTask;
    tasks.push(docTask);
    
    // 6. 部署任务
    let deployTask: ExtendedTask = {
      ...baseTask,
      id: `task-${Date.now()}-deploy`,
      content: `部署上线：${options.title}`,
      status: 'todo',
      priority: 'high' as TaskPriority,
      type: 'deployment',
      tags: ['部署', 'devops', 'prd'],
      estimate: '1d',
      dependsOn: [testTask.id], // 依赖测试任务
      acceptanceCriteria: [
        'WHEN 部署完成 THEN 产品在生产环境正常运行',
        'WHEN 监控配置完成 THEN 可以实时监控系统状态',
      ],
      aiMode: options.aiMode || 'auto',
    };
    
    deployTask = await this.assignAgentToTask(deployTask, options);
    deployTask = await this.taskStore.addTask(deployTask) as ExtendedTask;
    tasks.push(deployTask);
    
    logger.info(`Created ${tasks.length} tasks for PRD ${prdId}`);
    
    // 建立 PRD 与任务的关联
    if (tasks.length > 0) {
      await this.prdTaskAssociation.initialize();
      const taskIds = tasks.map(t => t.id);
      await this.prdTaskAssociation.associatePRDWithTasks(prdId, taskIds);
      logger.info(`Associated ${tasks.length} tasks with PRD ${prdId}`);
    }
    
    return tasks;
  }
  
  /**
   * 保存 PRD 到文件
   */
  async savePRD(prdId: string, content: string): Promise<string> {
    const prdDir = path.join(this.workflowDir, '.workflow', 'docs', 'prds');
    await fs.ensureDir(prdDir);
    
    const filename = `${prdId}.md`;
    const filepath = path.join(prdDir, filename);
    
    await fs.writeFile(filepath, content, 'utf-8');
    logger.info(`PRD saved to ${filepath}`);
    
    return filepath;
  }
}